import React, { Fragment, useEffect, useState } from 'react'
import { Add, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material'
import { Checkbox, IconButton, Input, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { createStyles, withStyles } from '@mui/styles'
import { MuiProps, defaultColors, defaultStyles, muiOptions } from '../../infrastructure/materialUiThemeProvider'
import { t } from '../../infrastructure/i18nextHelper'
import { api } from '../../infrastructure/api'
import { formatFilename } from '../../infrastructure/excelExport'
import { Button, DatePicker, LabelButton, MultipleSelect, SearchField, Select } from '../common/customComponents'
import moment from 'moment'
import { UserSettingsContextContainer } from '../../infrastructure/shell/userSettings/userSettingsStore'
import { tableStyles } from '../common/components/table/table'
import { useFilter } from '../common/useFilter'
import type { Item } from '../common/components/select/types'
import { Company, Site } from '../administration/masterData/models'
import { queryStringBuilder } from '../../infrastructure/queryStringBuilder'
import { interval, Subject } from 'rxjs'
import { debounce, filter, switchMap } from 'rxjs/operators'
import { popupNavigator } from '../../infrastructure/popupNavigator'
import { RecipeEditContainer, recipeDialog } from './recipeEditStore'
import {
    RoadSalesProduct,
    RoadSalesProductRecipe,
    RoadSalesProductRecipeWithOpenState,
    SearchRoadSalesProductRecipe
} from './recipeModels'

let allSitesFilterValue = '__all_sites__'

function Recipes({ classes }: MuiProps) {
    let settings = UserSettingsContextContainer.useContainer()
    let recipeEdit = RecipeEditContainer.useContainer()
    let dateFormat = settings.getDateFormat()
    let [recipes, setRecipes] = useState<RoadSalesProductRecipeWithOpenState[]>([])
    let [descriptionSearch, setDescriptionSearch] = useState('')
    let [activeSearch, setActiveSearch] = useState('')
    let sitesFilter = useFilter<Site>(sites => sites.map(site => ({ value: site.code, text: site.name })))
    let roadSalesProductsFilter = useFilter<RoadSalesProduct>(products => products.map(product => ({
        value: product.id,
        text: recipeEdit.oneCompanyInCountry
            ? `${product.code} - ${product.description}`
            : `${product.companyCode} - ${product.code} - ${product.description}`
    })))
    let validWhereSiteChoices = (sitesFilter.choices as Item[]).filter(choice => choice.value !== allSitesFilterValue)
    let [validAt, setValidAt] = useState<string | null>(null)
    let [validWhereSite, setValidWhereSite] = useState<string | null>(null)
    let [referentialsLoaded, setReferentialsLoaded] = useState(false)
    let [selectedRecipeIds, setSelectedRecipeIds] = useState<string[]>([])
    let [recipeQuerySubject] = useState(() => new Subject<{ filters: SearchRoadSalesProductRecipe }>())
    let getActiveValue = (active: boolean) => active ? 'Y' : 'N'
    let normalizedDescriptionSearch = descriptionSearch.toLowerCase()
    let normalizedActiveSearch = activeSearch.toLowerCase()
    let filteredRecipes = recipes.filter(recipe =>
        recipe.description.toLowerCase().includes(normalizedDescriptionSearch)
        && getActiveValue(recipe.active).toLowerCase().includes(normalizedActiveSearch))
    let selectedRecipes = recipes.filter(recipe => selectedRecipeIds.includes(recipe.id))
    let areAllRecipesSelected = recipes.length > 0 && selectedRecipes.length === recipes.length
    let areSomeRecipesSelected = selectedRecipes.length > 0
    let areAllRecipesOpen = recipes.length > 0 && recipes.every(recipe => recipe.isOpen)

    let getSearchFilters = (): SearchRoadSalesProductRecipe => ({
        sites: sitesFilter.values().filter(site => site !== allSitesFilterValue),
        roadSalesProductIds: roadSalesProductsFilter.values(),
        validAt,
        validWhereSite
    })

    let loadRecipes = () => recipeQuerySubject.next({
        filters: getSearchFilters(),
    })

    let loadReferentials = async () => {
        let [sites, roadSalesProducts, companies] = await Promise.all([
            api.get<Site[]>('masterdata/roadSalesProductRecipe/site'),
            api.get<RoadSalesProduct[]>('masterdata/roadSalesProductRecipe/roadSalesProduct'),
            api.get<Company[]>('comment/company')
        ])
        sitesFilter.setItems([...sites, { code: allSitesFilterValue, name: t('recipes.form.allSites') }])
        roadSalesProductsFilter.setItems(roadSalesProducts)
        recipeEdit.setOneCompanyInCountry(companies.length === 1)
        setReferentialsLoaded(true)
    }

    useEffect(() => {
        if (roadSalesProductsFilter.items.length > 0)
            roadSalesProductsFilter.setItems(roadSalesProductsFilter.items)
    }, [recipeEdit.oneCompanyInCountry])

    let isValidSearchArgs = ({ filters }: { filters: SearchRoadSalesProductRecipe }) =>
        Array.isArray(filters.sites)
        && Array.isArray(filters.roadSalesProductIds)
        && (filters.validAt === null || moment(filters.validAt).isValid())

    let searchRecipes = async (filters: SearchRoadSalesProductRecipe): Promise<RoadSalesProductRecipeWithOpenState[]> => {
        let queryString = queryStringBuilder({
            sites: filters.sites,
            roadSalesProductIds: filters.roadSalesProductIds,
            ...(filters.validAt ? { validAt: filters.validAt } : {}),
            ...(filters.validWhereSite ? { ValidWhereSite: filters.validWhereSite } : {})
        }, 'repeat')

        let loadedRecipes = await api.get<RoadSalesProductRecipe[]>(`masterdata/roadSalesProductRecipe${queryString}`)
        return loadedRecipes.map(recipe => ({ ...recipe, isOpen: false }))
    }

    let toggleRecipe = (recipeId: RoadSalesProductRecipe['id']) => setRecipes(currentRecipes =>
        currentRecipes.map(recipe => recipe.id === recipeId ? { ...recipe, isOpen: !recipe.isOpen } : recipe))

    let toggleAllRecipes = () => setRecipes(currentRecipes =>
        currentRecipes.map(recipe => ({ ...recipe, isOpen: !areAllRecipesOpen })))

    useEffect(() => {
        loadReferentials()
    }, [])

    useEffect(() => {
        let recipeQuerySubscription = recipeQuerySubject.asObservable()
            .pipe(
                filter(isValidSearchArgs),
                debounce(() => interval(1)),
                switchMap(async ({ filters }) => {
                    try {
                        return await searchRecipes(filters)
                    } catch {
                        return undefined
                    }
                }),
                filter((result): result is RoadSalesProductRecipeWithOpenState[] => result !== undefined)
            )
            .subscribe(loadedRecipes => {
                setRecipes(loadedRecipes)
                setSelectedRecipeIds([])
            })

        return () => recipeQuerySubscription.unsubscribe()
    }, [])

    useEffect(() => {
        if (referentialsLoaded)
            loadRecipes()
    }, [referentialsLoaded, sitesFilter.selectedChoices, roadSalesProductsFilter.selectedChoices, validAt, validWhereSite])

    useEffect(() => {
        if (!recipeEdit.isOpen && recipeEdit.previousIsOpen)
            loadRecipes()
    }, [recipeEdit.isOpen, recipeEdit.previousIsOpen])

    let exportExcel = async () => {
        let filename = formatFilename('RoadSalesProductRecipe.xlsx')
        await api.post('masterdata/roadSalesProductRecipe/export', {
            filename,
            filters: getSearchFilters(),
            recipeIds: selectedRecipes.length ? selectedRecipes.map(x => x.id) : null
        })
        await api.openDownload(filename)
    }

    let importExcel = async (file: Blob) => {
        await api.upload('masterdata/roadSalesProductRecipe/import', file, 'file', { withReport: 'dialog' })
        loadRecipes()
    }

    return (
        <div className={classes.pageContainer}>
            <div className={classes.firstRow}>
                <Typography className={classes.pageTitle} variant='h5' display='block' gutterBottom>{t('recipes.name')}</Typography>
                <div className={classes.actions}>
                    <Paper className={classes.filterPaper}>
                        <MultipleSelect
                            label={t('supplyJournal.filter.sites')}
                            choices={sitesFilter.choices}
                            values={sitesFilter.selectedChoices}
                            onChange={sitesFilter.setSelectedChoices} />
                        <MultipleSelect
                            label={t('recipes.table.roadSalesProduct')}
                            choices={roadSalesProductsFilter.choices}
                            values={roadSalesProductsFilter.selectedChoices}
                            onChange={roadSalesProductsFilter.setSelectedChoices} />
                        <Select
                            label={t('recipes.table.validWhere')}
                            choices={validWhereSiteChoices}
                            value={validWhereSite}
                            onChange={site => setValidWhereSite(site || null)}
                            isClearable />
                        <DatePicker
                            label={t('recipes.table.validAt')}
                            date={validAt}
                            setDate={setValidAt}
                            classesOverride={{ datepicker: classes.datePickerBottomMargin }} />
                        <Button
                            img='/static/images/excel_red.svg'
                            label={t('recipes.actions.export')}
                            onClick={exportExcel}
                            className={classes.secondaryButton} />
                        <LabelButton
                            img='/static/images/excel_red.svg'
                            label={t('recipes.actions.import')}
                            className={classes.secondaryButton}>
                            <Input
                                style={{ display: 'none' }}
                                type='file'
                                inputProps={{ accept: '.xls,.xlsx' }}
                                onChange={event => {
                                    let file = (event.target as HTMLInputElement).files?.[0]
                                    if (file) importExcel(file)
                                    event.target.value = ''
                                }} />
                        </LabelButton>
                        <Button
                            img={<Add />}
                            label={t('recipes.actions.create')}
                            onClick={() => recipeDialog.openNew()}
                            className={classes.primaryButton} />
                    </Paper>
                </div>
            </div>
            <Paper className={classes.content}>
                <Table stickyHeader aria-label={t('recipes.name')}>
                    <TableHead className={classes.tableHeader}>
                        <TableRow>
                            <TableCell className={classes.cellPadding}>
                                <Checkbox
                                    checked={areAllRecipesSelected}
                                    indeterminate={areSomeRecipesSelected && !areAllRecipesSelected}
                                    onChange={event => setSelectedRecipeIds(event.target.checked ? recipes.map(recipe => recipe.id) : [])}
                                    inputProps={{ 'aria-label': t('recipes.table.selectAll') }}
                                    classes={{ checked: classes.checkboxChecked, root: classes.noPadding }}
                                    color='default' />
                            </TableCell>
                            <TableCell className={classes.cellPadding}>
                                <IconButton
                                    className={classes.toggleButton}
                                    aria-label={t(areAllRecipesOpen ? 'recipes.table.collapseAll' : 'recipes.table.expandAll')}
                                    onClick={toggleAllRecipes}>
                                    {areAllRecipesOpen ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                                </IconButton>
                            </TableCell>
                            <TableCell className={classes.cellPadding}>{t('recipes.table.roadSalesProduct')}</TableCell>
                            <TableCell className={classes.cellPadding}>{t('recipes.table.recipeDescription')}</TableCell>
                            <TableCell className={classes.cellPadding}>{t('recipes.table.from')}</TableCell>
                            <TableCell className={classes.cellPadding}>{t('recipes.table.site')}</TableCell>
                            <TableCell className={classes.cellPadding}>{t('recipes.table.active')}</TableCell>
                            <TableCell className={classes.cellPadding}>{t('recipes.table.product')}</TableCell>
                            <TableCell className={classes.cellPadding}>{t('recipes.table.percent')}</TableCell>
                        </TableRow>
                        <TableRow data-exclude='true'>
                            <TableCell className={classes.filterHeadCell} />
                            <TableCell className={classes.filterHeadCell} />
                            <TableCell className={classes.filterHeadCell} />
                            <TableCell className={classes.filterHeadCell}>
                                <SearchField
                                    label=''
                                    text={descriptionSearch}
                                    onChange={event => setDescriptionSearch(event.target.value)}
                                    overrideStyle={{ root: classes.filterField }} />
                            </TableCell>
                            <TableCell className={classes.filterHeadCell} />
                            <TableCell className={classes.filterHeadCell} />
                            <TableCell className={classes.filterHeadCell}>
                                <SearchField
                                    label=''
                                    text={activeSearch}
                                    onChange={event => setActiveSearch(event.target.value)}
                                    overrideStyle={{ root: classes.filterField }} />
                            </TableCell>
                            <TableCell className={classes.filterHeadCell} />
                            <TableCell className={classes.filterHeadCell} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRecipes.map(recipe =>
                            <Fragment key={recipe.id}>
                                <TableRow className={classes.mainlineHover} onClick={() => popupNavigator.open('recipe', recipe.id)}>
                                    <TableCell className={classes.cellPadding} onClick={event => event.stopPropagation()}>
                                        <Checkbox
                                            checked={selectedRecipeIds.includes(recipe.id)}
                                            onChange={event => setSelectedRecipeIds(currentIds => event.target.checked
                                                ? [...currentIds, recipe.id]
                                                : currentIds.filter(id => id !== recipe.id))}
                                            inputProps={{ 'aria-label': t('recipes.table.selectRecipe', { product: recipe.roadSalesProductCode ?? recipe.id }) }}
                                            classes={{ checked: classes.checkboxChecked, root: classes.noPadding }}
                                            color='default' />
                                    </TableCell>
                                    <TableCell className={classes.cellPadding} onClick={event => event.stopPropagation()}>
                                        <IconButton
                                            className={classes.toggleButton}
                                            aria-label={t(recipe.isOpen ? 'recipes.table.collapseRecipe' : 'recipes.table.expandRecipe', { product: recipe.roadSalesProductCode ?? recipe.id })}
                                            aria-expanded={recipe.isOpen}
                                            onClick={() => toggleRecipe(recipe.id)}>
                                            {recipe.isOpen ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell className={classes.cellPadding}>
                                        {recipeEdit.oneCompanyInCountry
                                            ? recipe.roadSalesProductCode
                                            : `${recipe.companyCode} - ${recipe.roadSalesProductCode}`}
                                    </TableCell>
                                    <TableCell className={classes.cellPadding}>{recipe.description}</TableCell>
                                    <TableCell className={classes.cellPadding}>{recipe.validFrom ? moment(recipe.validFrom).format(dateFormat) : ''}</TableCell>
                                    <TableCell className={classes.cellPadding}>{recipe.site ?? ''}</TableCell>
                                    <TableCell className={classes.cellPadding}>{getActiveValue(recipe.active)}</TableCell>
                                    <TableCell className={classes.cellPadding}></TableCell>
                                    <TableCell className={classes.cellPadding}></TableCell>
                                </TableRow>
                                {recipe.isOpen && recipe.items.map((item, index) =>
                                    <TableRow key={item.id ?? `${recipe.id}-${item.productId ?? index}`} className={classes.subLine + ' ' + classes.sublineHover}>
                                        <TableCell className={classes.cellPadding}></TableCell>
                                        <TableCell className={classes.cellPadding}></TableCell>
                                        <TableCell className={classes.cellPadding}></TableCell>
                                        <TableCell className={classes.cellPadding}></TableCell>
                                        <TableCell className={classes.cellPadding}></TableCell>
                                        <TableCell className={classes.cellPadding}></TableCell>
                                        <TableCell className={classes.cellPadding}></TableCell>
                                        <TableCell className={classes.cellPadding}>{item.productCode ?? ''}</TableCell>
                                        <TableCell className={classes.cellPadding}>{item.percentage === null ? '' : item.percentage * 100}</TableCell>
                                    </TableRow>)}
                            </Fragment>)}
                    </TableBody>
                </Table>
            </Paper>
        </div>
    )
}

let styles = () =>
    createStyles({
        pageContainer: {
            display: 'grid',
            height: '100%',
            width: '100%',
            paddingBottom: '0.5em',
            'grid-template-areas': "'firstRow' 'secondRow'",
            'grid-template-columns': 'auto',
            'grid-template-rows': '5em minmax(0, 1fr)',
            'grid-gap': '0.8em',
            overflow: 'hidden',
            'box-sizing': 'border-box',
        },
        firstRow: {
            ...defaultStyles.flexRow,
            alignItems: 'center',
            justifyContent: 'space-between',
            'grid-area': 'firstRow',
            minWidth: 0
        },
        actions: {
            ...defaultStyles.flexRow,
            alignItems: 'center',
            justifyContent: 'flex-end',
            minWidth: 0,
            gap: '0.5em'
        },
        filterPaper: {
            ...defaultStyles.flexRow,
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.75em',
            padding: '0.75em 0.75em 0.25em 0.5em',
            marginTop: '0.5em',
            marginRight: '0.75em',
            minWidth: 0,
            boxSizing: 'border-box',
        },
        datePickerBottomMargin: {
            marginBottom: '0.35em',
        },
        pageTitle: {
            color: defaultColors.grey.dark.color,
            margin: '0',
        },
        primaryButton: {
            ...defaultStyles.primaryButton
        },
        secondaryButton: {
            ...defaultStyles.secondaryButton
        },
        tableHeader: {
            position: 'sticky',
            zIndex: 100
        },
        cellPadding: tableStyles.cellPadding,
        cellRightPadding: tableStyles.cellRightPadding,
        filterHeadCell: tableStyles.filterHeadCell,
        filterField: tableStyles.filterField,
        toggleButton: {
            margin: '-0.8em -0.5em',
            '& > svg': {
                width: '0.8em',
            }
        },
        checkboxChecked: tableStyles.checkboxChecked,
        noPadding: tableStyles.noPadding,
        subLine: {
            backgroundColor: 'whitesmoke',
        },
        mainlineHover: {
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: '#FBFBFB'
            }
        },
        sublineHover: {
            '&:hover': {
                backgroundColor: '#e6e6e6'
            }
        },
        content: {
            'grid-area': 'secondRow',
            minWidth: 0,
            minHeight: 0,
            overflow: 'auto',
        },
    })

export default withStyles(styles, muiOptions)(Recipes)
